import { normalizeMapStyle, type MapStyleId } from '../../../renderer/src/map-style';
export type { MapStyleId } from '../../../renderer/src/map-style';

/** Map style offered by the production visual preview. */
export interface MapStyleDefinition {
  readonly id: MapStyleId;
  readonly label: string;
  readonly terrain?: TerrainDefinition;
  readonly url?: string;
  readonly imagery?: "mapbox-satellite";
  readonly treatment?: 'blackbird';
  readonly previewOnly?: boolean;
}

/** DEM configuration applied to a vector style for shaded relief. */
export interface TerrainDefinition {
  readonly attribution: string;
  readonly encoding: "terrarium";
  readonly exaggeration: number;
  readonly mode: TerrainMode;
  readonly tileJsonUrl: string;
  readonly tileUrlTemplate: string;
}

/** Visual treatments derived from the shared elevation model. */
export type TerrainMode = "moon-base" | "heat" | "relief" | "topographic" | "terra";

export const TERRAIN_PROVIDER = {
  attribution: "<a href='https://mapterhorn.com/attribution'>© Mapterhorn</a>",
  encoding: "terrarium" as const,
  tileJsonUrl: "https://tiles.mapterhorn.com/tilejson.json",
  tileUrlTemplate: "https://tiles.mapterhorn.com/{z}/{x}/{y}.webp",
};

/**
 * Creates one immutable rendering treatment over the allowlisted terrain source.
 *
 * @param mode - Terrain presentation selected by the user.
 * @param exaggeration - Visual mesh exaggeration applied by MapLibre.
 * @returns Complete terrain presentation definition.
 */
function terrain(mode: TerrainMode, exaggeration: number): TerrainDefinition {
  return { ...TERRAIN_PROVIDER, exaggeration, mode };
}

/** Allowlisted basemaps; Terra requires a configured public Mapbox token. */
export const MAP_STYLES: readonly MapStyleDefinition[] = [
  { id: "mcnally", label: "McNally", url: "https://tiles.openfreemap.org/styles/bright" },
  { id: "positron", label: "Positron", url: "https://tiles.openfreemap.org/styles/positron" },
  {
    id: "relief",
    label: "Structures",
    terrain: terrain("relief", 1.15),
    url: "https://tiles.openfreemap.org/styles/liberty",
  },
  {
    id: "topographic",
    label: "Topo",
    terrain: terrain("topographic", 1.05),
    url: "https://tiles.openfreemap.org/styles/positron",
  },
  {
    id: "heat",
    label: "Heat",
    terrain: terrain("heat", 1.08),
    url: "https://tiles.openfreemap.org/styles/liberty",
  },
  {
    id: "moon-base",
    label: "Moon Base",
    terrain: terrain("moon-base", 1.18),
    url: "https://tiles.openfreemap.org/styles/fiord",
  },
  {
    id: "terra",
    label: "Terra",
    imagery: "mapbox-satellite",
    terrain: terrain("terra", 1.15),
  },
  {
    id: 'blackbird', label: 'Blackbird', imagery: 'mapbox-satellite', treatment: 'blackbird',
    terrain: terrain('terra', 1),
  },
] as const;

/**
 * Resolves user input against the fixed style allowlist.
 *
 * @param candidate - Candidate style identifier from a form control.
 * @returns Matching style, or McNally when the identifier is unknown.
 */
export function resolveMapStyle(candidate: string): MapStyleDefinition {
  const id = normalizeMapStyle(candidate);
  return MAP_STYLES.find((style) => style.id === id) ?? MAP_STYLES[0]!;
}
